import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { WarningAmberRounded, BusinessRounded, Search } from '@mui/icons-material';

export default function UncreatedSchedulesAlert({ reportData = [], stateId }) {
  // Local frontend text matching control
  const [searchTerm, setSearchTerm] = useState('');

  // 1. Initial State Filter Check Block
  const filteredData = stateId
    ? reportData.filter((r) => String(r.stateId) === String(stateId))
    : reportData;

  // 2. Isolate facilities completely lacking created report records
  const defaultingFacilities = filteredData.filter(
    (r) => !r.reportId || r.plannedReportStatus?.toLowerCase() === 'not planned'
  );

  // 3. Frontend Search filtering execution logic
  const searchedFacilities = defaultingFacilities.filter((facility) =>
    facility.facility ? facility.facility.toLowerCase().includes(searchTerm.toLowerCase()) : true
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* FULLY RESPONSIVE HEADER SECTION PANEL CONTEXT */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <WarningAmberRounded sx={{ color: '#ff4842' }} />
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Action Required: Missing Schedules ({defaultingFacilities.length})
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary" display="block">
            Facilities that have not initialized or planned their mandatory quarterly monitoring
            workflows
          </Typography>
        </Box>

        {/* FRONTEND SEARCH TEXTFIELD INPUT BLOCK (Responsive Widths) */}
        {defaultingFacilities.length > 0 && (
          <TextField
            size="small"
            placeholder="Search facility..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: '10px', bgcolor: '#f9fafb' },
            }}
            sx={{ width: { xs: '100%', sm: '180px' } }}
          />
        )}
      </Box>

      {/* SCROLL CONTAINER */}
      <Stack spacing={2} sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5, mt: 1, flexGrow: 1 }}>
        {searchedFacilities.map((facility, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha('#ff4842', 0.02),
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' }, // Adaptive stack inside rows
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              transition: 'transform 0.2s, border-color 0.2s',
              '&:hover': { transform: 'translateX(4px)', borderColor: alpha('#ff4842', 0.2) },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha('#ff4842', 0.1),
                  color: '#ff4842',
                  borderRadius: '8px',
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                }}
              >
                <BusinessRounded />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {facility.facility}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  Sector: {facility.sectorName || 'Unassigned'} • Area: {facility.stateName}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label="No Profile"
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.65rem',
                bgcolor: alpha('#ff4842', 0.1),
                color: '#ff4842',
                borderRadius: '6px',
                alignSelf: { xs: 'flex-end', sm: 'center' }, // Aligns neatly inside mobile/desktop layouts
              }}
            />
          </Box>
        ))}

        {/* EMPTY HANDLING MATRICES */}
        {defaultingFacilities.length > 0 && searchedFacilities.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled', fontStyle: 'italic' }}>
            No defaulting facilities match your search query text string.
          </Box>
        )}

        {defaultingFacilities.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.disabled', fontStyle: 'italic' }}>
            All facilities have created active quarter schedules.
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
